import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMfaVerify, useLogout, sessionKey, type Session } from '@/shared/api/session';
import { errorMessage, post, setCsrfToken } from '@/shared/api/client';
import { Alert, Button, Card, Field, Input } from '@/shared/ui';
import { routeForState } from '@/app/guards';

/** Verificación MFA al iniciar sesión (state=mfa_pending) o step-up para acciones de alto impacto. */
export function MfaVerifyPage({ stepup = false }: { stepup?: boolean }) {
  const verify = useMfaVerify(stepup ? '/auth/mfa/stepup' : '/auth/mfa/verify');
  const logout = useLogout();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm<{ code: string }>();
  const onSubmit = handleSubmit(async ({ code }) => {
    try {
      const s = await verify.mutateAsync(code.trim());
      if (stepup) navigate(-1);
      else navigate(routeForState(s.state) ?? '/');
    } catch { /* error abajo */ }
  });
  return (
    <Card title={stepup ? 'Confirma tu identidad' : 'Verificación en dos pasos'}>
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--sgr-text-secondary)' }}>{stepup ? 'Esta acción es de alto impacto. Ingresa el código de tu aplicación de autenticación.' : 'Ingresa el código de 6 dígitos de tu aplicación de autenticación, o un código de recuperación.'}</p>
      {verify.isError && <Alert kind="error">{errorMessage(verify.error)}</Alert>}
      <form onSubmit={onSubmit} noValidate>
        <Field label="Código" htmlFor="code" error={errors.code?.message}>
          <Input id="code" mono autoComplete="one-time-code" inputMode="numeric" autoFocus maxLength={32} invalid={!!errors.code} {...register('code', { required: 'Ingresa el código' })} />
        </Field>
        <Button type="submit" block loading={verify.isPending}>Verificar</Button>
      </form>
      {!stepup && <Button variant="ghost" block style={{ marginTop: 'var(--sp-3)' }} onClick={() => logout.mutate(undefined, { onSettled: () => navigate('/auth/login', { replace: true }) })}>Cancelar y cerrar sesión</Button>}
    </Card>
  );
}

/** Enrolamiento obligatorio para roles privilegiados (state=mfa_enrollment_required) o autoservicio. */
export function MfaEnrollPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = useLogout();
  const [uri, setUri] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const start = useMutation({ mutationFn: () => post<{ otpauth_uri: string }>('/auth/mfa/enroll'), onSuccess: (r) => setUri(r.otpauth_uri) });
  const confirm = useMutation({
    mutationFn: (code: string) => post<{ recovery_codes: string[]; state: Session['state'] }>('/auth/mfa/enroll/confirm', { code }),
    onSuccess: async (r) => { setCodes(r.recovery_codes); const s = await fetchSession(); if (s) qc.setQueryData(sessionKey, s); },
  });
  const { register, handleSubmit } = useForm<{ code: string }>();
  const secret = uri ? new URL(uri).searchParams.get('secret') : null;

  if (codes) {
    return (
      <Card title="Guarda tus códigos de recuperación">
        <Alert kind="warning">Se muestran una sola vez. Guárdalos en un lugar seguro: cada uno sirve para ingresar una única vez si pierdes tu teléfono.</Alert>
        <ul style={{ fontFamily: 'var(--sgr-font-mono)', columns: 2, fontSize: 'var(--fs-md)' }}>{codes.map((c) => <li key={c}>{c}</li>)}</ul>
        <Button block onClick={() => { const s = qc.getQueryData<Session | null>(sessionKey); navigate(routeForState(s?.state) ?? '/', { replace: true }); }}>Ya los guardé, continuar</Button>
      </Card>
    );
  }
  return (
    <Card title="Configura la verificación en dos pasos">
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--sgr-text-secondary)' }}>Tu rol requiere un segundo factor (TOTP). Usa una aplicación como Google Authenticator, Microsoft Authenticator o 1Password.</p>
      {!uri ? (
        <>
          {start.isError && <Alert kind="error">{errorMessage(start.error)}</Alert>}
          <Button block loading={start.isPending} onClick={() => start.mutate()}>Generar clave</Button>
        </>
      ) : (
        <>
          <ol style={{ fontSize: 'var(--fs-sm)', paddingLeft: 'var(--sp-5)' }}>
            <li>Abre tu aplicación de autenticación y elige "agregar cuenta" por clave manual (o escanea si tu app lo permite desde este enlace):</li>
          </ol>
          <p><a href={uri} style={{ wordBreak: 'break-all', fontSize: 'var(--fs-xs)' }}>{uri}</a></p>
          <p style={{ fontFamily: 'var(--sgr-font-mono)', fontSize: 'var(--fs-lg)', letterSpacing: '.1em', textAlign: 'center', userSelect: 'all' }}>{secret}</p>
          {confirm.isError && <Alert kind="error">{errorMessage(confirm.error)}</Alert>}
          <form onSubmit={handleSubmit(({ code }) => confirm.mutate(code.trim()))} noValidate>
            <Field label="Ingresa el código que muestra la app" htmlFor="code">
              <Input id="code" mono inputMode="numeric" autoComplete="one-time-code" maxLength={6} {...register('code', { required: true })} />
            </Field>
            <Button type="submit" block loading={confirm.isPending}>Activar</Button>
          </form>
        </>
      )}
      <Button variant="ghost" block style={{ marginTop: 'var(--sp-3)' }} onClick={() => logout.mutate(undefined, { onSettled: () => navigate('/auth/login', { replace: true }) })}>Cancelar y cerrar sesión</Button>
    </Card>
  );
}

async function fetchSession(): Promise<Session | null> {
  try {
    const s = await (await import('@/shared/api/client')).get<Session>('/auth/session');
    setCsrfToken(s.csrf_token);
    return s;
  } catch {
    return null;
  }
}
